import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Settings as SettingsIcon, Coins, CircleDollarSign, Check, X, Loader2, Zap, User, Volume2 } from "lucide-react";
import { useSiteSettings } from "@/lib/settings";
import { BodySilhouetteAnatomical, BODY_DIAGRAM_STYLES } from "@/components/report/BodySilhouetteAnatomical";

export default function AdminPlatformSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [topUps, setTopUps] = useState([]);
  const [tab, setTab] = useState("pending");
  const [loadingTopUps, setLoadingTopUps] = useState(false);
  const [rzp, setRzp] = useState(null);          // current Razorpay config as returned by admin GET
  const [rzpEdit, setRzpEdit] = useState({});    // pending edits from the form
  const [savingRzp, setSavingRzp] = useState(false);
  const { refresh: refreshSiteSettings } = useSiteSettings();

  const loadSettings = async () => {
    try { const r = await api.get("/platform-settings"); setS(r.data); }
    catch (err) { toast.error(apiError(err, "Could not load settings")); }
  };
  const loadRzp = async () => {
    try { const r = await api.get("/admin/razorpay-config"); setRzp(r.data); setRzpEdit({}); }
    catch (err) { toast.error(apiError(err, "Could not load Razorpay config")); }
  };
  const loadTopUps = async () => {
    setLoadingTopUps(true);
    try { const r = await api.get(`/admin/top-ups?status=${tab}`); setTopUps(r.data.items || []); }
    catch (err) { toast.error(apiError(err, "Could not load top-ups")); }
    finally { setLoadingTopUps(false); }
  };
  useEffect(() => { loadSettings(); loadRzp(); }, []);
  useEffect(() => { loadTopUps(); }, [tab]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/platform-settings", s);
      setS(r.data);
      toast.success("Platform settings saved");
      refreshSiteSettings();
    } catch (err) { toast.error(apiError(err, "Save failed")); }
    finally { setSaving(false); }
  };

  const approve = async (id) => {
    try { await api.post(`/admin/top-ups/${id}/approve`); toast.success("Top-up approved & coins credited"); loadTopUps(); }
    catch (err) { toast.error(apiError(err, "Approve failed")); }
  };
  const reject = async (id) => {
    const reason = window.prompt("Reject reason? (shown to patient)");
    if (reason === null) return;
    try { await api.post(`/admin/top-ups/${id}/reject`, { reason: reason || "Rejected" }); toast.success("Rejected"); loadTopUps(); }
    catch (err) { toast.error(apiError(err, "Reject failed")); }
  };

  const saveRzp = async () => {
    if (!rzpEdit || Object.keys(rzpEdit).length === 0) { toast.info("No Razorpay changes to save"); return; }
    setSavingRzp(true);
    try {
      const r = await api.put("/admin/razorpay-config", rzpEdit);
      setRzp(r.data);
      setRzpEdit({});
      toast.success("Razorpay config saved — changes are live");
    } catch (err) { toast.error(apiError(err, "Razorpay save failed")); }
    finally { setSavingRzp(false); }
  };
  const setRzpField = (k, v) => setRzpEdit((prev) => ({ ...prev, [k]: v }));

  if (!s) return <DashboardLayout role="super_admin"><div className="p-8"><Loader2 className="w-6 h-6 animate-spin text-ink/40" /></div></DashboardLayout>;

  const set = (k, v) => setS({ ...s, [k]: v });

  return (
    <DashboardLayout role="super_admin">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-bold text-3xl text-ink flex items-center gap-2"><SettingsIcon className="w-6 h-6" /> Platform Settings</h1>
          <p className="text-ink/60">Marketing site toggles, coin economy, UPI top-up config.</p>
        </div>
        <Button data-testid="save-platform" onClick={save} disabled={saving} className="bg-ink text-white hover:bg-ink/90">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save settings</Button>
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        {/* Marketing site toggles */}
        <Card className="p-6 rounded-2xl" data-testid="marketing-toggles">
          <div className="font-heading font-semibold text-ink">Marketing website visibility</div>
          <p className="text-sm text-ink/60 mt-1">Individually hide any button, link or the entire marketplace on the public site.</p>
          <div className="mt-4 space-y-4">
            <div className="text-[10px] uppercase tracking-widest text-ink/40 font-semibold">Top-nav links</div>
            <ToggleRow label='"Home" link' value={s.show_home_link !== false} onChange={(v) => set("show_home_link", v)} testid="tg-home" />
            <ToggleRow label='"Book Tests" text link' value={s.show_book_tests_link !== false} onChange={(v) => set("show_book_tests_link", v)} testid="tg-booktests" />
            <ToggleRow label='"How it works" anchor' value={s.show_how_it_works_link !== false} onChange={(v) => set("show_how_it_works_link", v)} testid="tg-howitworks" />
            <ToggleRow label='"For Labs" link' value={s.show_for_labs_btn} onChange={(v) => set("show_for_labs_btn", v)} testid="tg-forlabs" />
            <div className="text-[10px] uppercase tracking-widest text-ink/40 font-semibold pt-2">Buttons</div>
            <ToggleRow label='"Book a Test" CTA button (orange)' value={s.show_book_test_btn} onChange={(v) => set("show_book_test_btn", v)} testid="tg-book" />
            <ToggleRow label='"Dashboard" button (visible when logged in)' value={s.show_dashboard_btn !== false} onChange={(v) => set("show_dashboard_btn", v)} testid="tg-dashboard" />
            <ToggleRow label='"Login / Sign up" buttons (visible when logged out)' value={s.show_login_link !== false} onChange={(v) => set("show_login_link", v)} testid="tg-login" />
            <div className="text-[10px] uppercase tracking-widest text-ink/40 font-semibold pt-2">Sections</div>
            <ToggleRow label="Marketplace (Book Tests page + lab listings)" value={s.marketplace_enabled} onChange={(v) => set("marketplace_enabled", v)} testid="tg-marketplace" hint="When off: /book-tests is hidden and no labs appear as marketplace-listed." />
          </div>
        </Card>

        {/* Coin economy */}
        <Card className="p-6 rounded-2xl" data-testid="coin-economy">
          <div className="font-heading font-semibold text-ink flex items-center gap-2"><Coins className="w-4 h-4 text-efgh-yellow" /> Coin economy</div>
          <p className="text-sm text-ink/60 mt-1">Fine-tune the wallet mechanics for the whole platform.</p>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div><Label>Report cost (coins)</Label><Input type="number" min={0} data-testid="ce-report-cost" value={s.report_cost_coins} onChange={(e) => set("report_cost_coins", parseInt(e.target.value, 10) || 0)} /></div>
            <div><Label>Signup bonus (coins)</Label><Input type="number" min={0} data-testid="ce-signup" value={s.signup_bonus_coins} onChange={(e) => set("signup_bonus_coins", parseInt(e.target.value, 10) || 0)} /></div>
            <div><Label>Referral reward (coins)</Label><Input type="number" min={0} data-testid="ce-referral" value={s.referral_reward_coins} onChange={(e) => set("referral_reward_coins", parseInt(e.target.value, 10) || 0)} /></div>
            <div><Label>Coins per ₹1</Label><Input type="number" min={1} data-testid="ce-coins-per-rupee" value={s.coins_per_rupee} onChange={(e) => set("coins_per_rupee", parseInt(e.target.value, 10) || 1)} /></div>
          </div>
        </Card>

        {/* Listen feature — TTS provider + language + kill-switch */}
        <Card className="p-6 rounded-2xl lg:col-span-2" data-testid="listen-settings">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div className="min-w-0">
              <div className="font-heading font-semibold text-ink flex items-center gap-2"><Volume2 className="w-4 h-4 text-efgh-blue" /> Listen (Text-to-Speech)</div>
              <p className="text-sm text-ink/60 mt-1">Play the AI Smart Health Summary as audio in English + one regional Indian language.</p>
            </div>
            <div className="flex items-center gap-2" data-testid="listen-master-toggle">
              <Label className="text-xs text-ink/70 cursor-pointer">Enable globally</Label>
              <Switch checked={s.listen_enabled !== false} onCheckedChange={(v) => set("listen_enabled", v)} data-testid="listen-enabled-switch" />
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-ink/60">Voice provider</Label>
              <div className="mt-2 grid grid-cols-2 gap-2" data-testid="tts-provider">
                {[
                  { id: "sarvam", label: "Sarvam AI", hint: "Best for Indian languages" },
                  { id: "openai", label: "OpenAI TTS-HD", hint: "Universal, English-first" },
                ].map((p) => {
                  const active = (s.tts_provider || "sarvam") === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => set("tts_provider", p.id)}
                      data-testid={`tts-provider-${p.id}`}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${active ? "border-ink bg-ink/5" : "border-border hover:bg-muted"}`}
                    >
                      <div className="font-heading font-semibold text-sm text-ink">{p.label}</div>
                      <div className="text-[11px] text-ink/50 mt-0.5">{p.hint}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-ink/60">Default regional language</Label>
              <p className="text-[10.5px] text-ink/40 mt-0.5">Applied when a lab hasn&apos;t set its own regional preference.</p>
              <div className="mt-2 grid grid-cols-3 sm:grid-cols-5 gap-1.5" data-testid="regional-lang-grid">
                {[
                  { id: "hi", label: "हिन्दी",   en: "Hindi"    },
                  { id: "pa", label: "ਪੰਜਾਬੀ",   en: "Punjabi"  },
                  { id: "gu", label: "ગુજરાતી",  en: "Gujarati" },
                  { id: "bn", label: "বাংলা",    en: "Bangla"   },
                  { id: "mr", label: "मराठी",    en: "Marathi"  },
                ].map((L) => {
                  const active = (s.default_regional_language || "hi") === L.id;
                  return (
                    <button
                      key={L.id}
                      type="button"
                      onClick={() => set("default_regional_language", L.id)}
                      data-testid={`default-lang-${L.id}`}
                      className={`p-2 rounded-lg border-2 transition-all ${active ? "border-ink bg-ink/5" : "border-border hover:bg-muted"}`}
                      title={L.en}
                    >
                      <div className="font-heading font-semibold text-sm text-ink truncate">{L.label}</div>
                      <div className="text-[10px] text-ink/50 truncate">{L.en}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <p className="text-[11px] text-ink/50 mt-3">Per-lab kill-switch lives on each lab&apos;s Branding page. When disabled globally the Listen button is hidden from every report.</p>
        </Card>

        {/* Body Diagram Style — global selector for all Smart Reports */}
        <Card className="p-6 rounded-2xl lg:col-span-2" data-testid="body-diagram-settings">
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <div className="font-heading font-semibold text-ink flex items-center gap-2"><User className="w-4 h-4 text-efgh-blue" /> Smart Report body diagram</div>
              <p className="text-sm text-ink/60 mt-1">Choose the silhouette style used on every Smart Report. Applied to both male &amp; female patients automatically.</p>
            </div>
            <div className="text-[11px] uppercase tracking-widest text-ink/50">Current: <span className="font-mono text-ink/80">{s.body_diagram_style || "apple_minimal"}</span></div>
          </div>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="body-diagram-grid">
            {BODY_DIAGRAM_STYLES.map((opt) => {
              const selected = (s.body_diagram_style || "apple_minimal") === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  data-testid={`body-style-${opt.id}`}
                  onClick={() => set("body_diagram_style", opt.id)}
                  className={`group relative rounded-2xl border-2 overflow-hidden transition-all ${selected ? "border-ink shadow-lg" : "border-border hover:border-ink/40"}`}
                  title={opt.hint}
                >
                  <div className="relative bg-gradient-to-b from-slate-50 to-white" style={{ paddingBottom: "140%" }}>
                    <BodySilhouetteAnatomical gender="male" style={opt.id} />
                  </div>
                  <div className="px-2 py-1.5 text-left border-t border-border/60 bg-white">
                    <div className="text-[11.5px] font-semibold text-ink truncate">{opt.label}</div>
                    <div className="text-[10px] text-ink/50 truncate">{opt.hint}</div>
                  </div>
                  {selected && (
                    <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-ink text-white grid place-items-center">
                      <Check className="w-3 h-3" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-ink/50 mt-3">Tip: the selected style applies to <b>every</b> Smart Report — the patient&apos;s own dashboard and the public share links (<code>/l/&lt;slug&gt;/r/&lt;id&gt;</code>).</p>
        </Card>

        {/* UPI settings */}
        <Card className="p-6 rounded-2xl lg:col-span-2" data-testid="upi-settings">
          <div className="font-heading font-semibold text-ink flex items-center gap-2"><CircleDollarSign className="w-4 h-4 text-efgh-green" /> UPI top-up config</div>
          <p className="text-sm text-ink/60 mt-1">Patients scan this UPI ID / QR to top up their wallet, then submit the UTR for verification.</p>
          <div className="mt-4 grid md:grid-cols-3 gap-3">
            <div><Label>UPI ID (VPA)</Label><Input data-testid="upi-id" value={s.upi_id || ""} onChange={(e) => set("upi_id", e.target.value)} placeholder="efghlabs@ybl" /></div>
            <div><Label>Display name</Label><Input data-testid="upi-name" value={s.upi_display_name || ""} onChange={(e) => set("upi_display_name", e.target.value)} placeholder="EFGH Health" /></div>
            <div><Label>Static QR image URL (optional)</Label><Input data-testid="upi-qr" value={s.upi_qr_url || ""} onChange={(e) => set("upi_qr_url", e.target.value)} placeholder="https://…/static-qr.png" /></div>
          </div>
          <div className="mt-2 text-[11px] text-ink/50">If no static QR URL is set, patients will see an auto-generated UPI QR pointing at your VPA.</div>
        </Card>

        {/* Razorpay config — DB-first with .env fallback */}
        <Card className="p-6 rounded-2xl lg:col-span-2" data-testid="razorpay-settings">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="font-heading font-semibold text-ink flex items-center gap-2"><Zap className="w-4 h-4 text-efgh-orange" /> Razorpay config</div>
              <p className="text-sm text-ink/60 mt-1">Live keys entered here override the deployment&apos;s environment variables and take effect instantly — no restart needed.</p>
            </div>
            <Button onClick={saveRzp} disabled={savingRzp || !rzp} data-testid="save-rzp" className="bg-ink text-white hover:bg-ink/90">
              {savingRzp && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save Razorpay config
            </Button>
          </div>
          {!rzp ? (
            <div className="mt-4 text-sm text-ink/50">Loading…</div>
          ) : (
            <>
              <div className="mt-3 text-[11px] uppercase tracking-widest text-ink/50 flex items-center gap-2">
                <span>Current source:</span>
                <span className={`px-2 py-0.5 rounded ${rzp.source === "database" ? "bg-efgh-green/10 text-efgh-green" : "bg-warning/15 text-warning"}`}>
                  {rzp.source === "database" ? "Database (super-admin override)" : "Environment (.env fallback)"}
                </span>
                <span className={`ml-auto px-2 py-0.5 rounded ${rzp.enabled ? "bg-success/10 text-success" : "bg-danger/10 text-danger"}`}>{rzp.enabled ? "Enabled" : "Disabled"}</span>
              </div>
              <div className="mt-4 grid md:grid-cols-2 gap-3">
                <div className="md:col-span-2 flex items-center gap-3">
                  <Switch data-testid="rzp-enabled" checked={rzpEdit.enabled ?? rzp.enabled} onCheckedChange={(v) => setRzpField("enabled", v)} />
                  <span className="text-sm text-ink">Accept Razorpay payments</span>
                </div>
                <div>
                  <Label>Key ID (public)</Label>
                  <Input data-testid="rzp-key-id" value={rzpEdit.key_id ?? rzp.key_id ?? ""} onChange={(e) => setRzpField("key_id", e.target.value)} placeholder="rzp_live_… or rzp_test_…" className="font-mono" />
                </div>
                <div>
                  <Label>Smart-report unlock fee (₹)</Label>
                  <Input data-testid="rzp-report-fee" type="number" min="0" value={rzpEdit.smart_report_fee_inr ?? rzp.smart_report_fee_inr ?? 49} onChange={(e) => setRzpField("smart_report_fee_inr", parseInt(e.target.value, 10) || 0)} />
                </div>
                <div>
                  <Label>Key Secret</Label>
                  <Input data-testid="rzp-key-secret" type="password" value={rzpEdit.key_secret ?? ""} onChange={(e) => setRzpField("key_secret", e.target.value)} placeholder={rzp.key_secret_masked || "Not set — leave blank to keep current"} className="font-mono" />
                  <p className="text-[11px] text-ink/50 mt-1">Currently: <span className="font-mono">{rzp.key_secret_masked || "— none —"}</span> · empty submit reverts to env.</p>
                </div>
                <div>
                  <Label>Webhook Secret <span className="text-ink/40">(optional)</span></Label>
                  <Input data-testid="rzp-webhook-secret" type="password" value={rzpEdit.webhook_secret ?? ""} onChange={(e) => setRzpField("webhook_secret", e.target.value)} placeholder={rzp.webhook_secret_masked || "Not set — leave blank to keep current"} className="font-mono" />
                  <p className="text-[11px] text-ink/50 mt-1">Currently: <span className="font-mono">{rzp.webhook_secret_masked || "— none —"}</span></p>
                </div>
              </div>
              <div className="mt-3 text-[11px] text-ink/50">
                ⓘ Leave a secret field blank to keep the existing value. Type a new secret to overwrite. To fully revert to <code className="font-mono">.env</code>, clear Key ID and set both secrets to blank spaces before saving.
              </div>
            </>
          )}
        </Card>
      </div>

      {/* Top-up requests inbox */}
      <Card className="mt-6 rounded-2xl overflow-hidden" data-testid="topup-inbox">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="font-heading font-semibold text-ink">Wallet top-up requests</div>
          <div className="flex rounded-md border border-border overflow-hidden text-xs">
            {["pending", "approved", "rejected", "all"].map((k) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`topup-tab-${k}`} className={`px-3 py-1 capitalize ${tab === k ? "bg-ink text-white" : "bg-white text-ink/60 hover:bg-muted"}`}>{k}</button>
            ))}
          </div>
        </div>
        {loadingTopUps ? (
          <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin text-ink/40 mx-auto" /></div>
        ) : topUps.length === 0 ? (
          <div className="p-10 text-center text-sm text-ink/50">No {tab} top-ups.</div>
        ) : (
          <div className="divide-y divide-border">
            {topUps.map((t) => (
              <div key={t.id} className="grid grid-cols-[1.5fr_1fr_1fr_1fr_auto] gap-3 px-6 py-3 items-center" data-testid={`topup-${t.id}`}>
                <div>
                  <div className="font-medium text-ink text-sm">{t.user?.full_name || t.user?.email || t.user_id}</div>
                  <div className="text-[11px] text-ink/50">{t.user?.email} · {t.user?.phone || "—"}</div>
                </div>
                <div><div className="font-heading font-semibold text-ink">₹{t.amount_rupees}</div><div className="text-[11px] text-ink/50">{t.coins} coins</div></div>
                <div className="text-xs font-mono text-ink/70">UTR: {t.upi_utr}</div>
                <div className="text-[11px] text-ink/50">{new Date(t.created_at).toLocaleString()}</div>
                <div className="flex gap-1.5 justify-end">
                  {t.status === "pending" ? (
                    <>
                      <Button size="sm" variant="outline" onClick={() => reject(t.id)} data-testid={`reject-${t.id}`}><X className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" onClick={() => approve(t.id)} data-testid={`approve-${t.id}`} className="bg-success hover:bg-success/90 text-white"><Check className="w-3.5 h-3.5 mr-1" /> Approve</Button>
                    </>
                  ) : (
                    <span className={`text-[11px] px-2 py-1 rounded ${t.status === "approved" ? "bg-success/10 text-success" : "bg-danger/10 text-danger"}`}>{t.status}{t.reject_reason && ` — ${t.reject_reason}`}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </DashboardLayout>
  );
}

function ToggleRow({ label, value, onChange, testid, hint }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0"><div className="text-sm text-ink font-medium">{label}</div>{hint && <div className="text-[11px] text-ink/50 mt-0.5">{hint}</div>}</div>
      <Switch data-testid={testid} checked={!!value} onCheckedChange={onChange} />
    </div>
  );
}
